import { useTranslation } from 'react-i18next';
import { trackTrendyolConversion } from '../../../hooks/useConversionTracking';

export default function Pricing() {
  const { t } = useTranslation();

  const scrollToOrder = () => {
    const element = document.getElementById('order');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const plans = [
    {
      name: t('pricing.yogaTowel'),
      price: '₺699',
      image: 'https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/da538d981033f3b11a51bdf49a2e437d.png',
      features: [
        t('pricing.feature1'),
        t('pricing.feature2'),
        t('pricing.feature3'),
        t('pricing.feature4'),
        t('pricing.feature5')
      ],
      popular: false,
      trendyolLink: 'https://ty.gl/w08pqn6i31wko'
    },
    {
      name: t('pricing.pilatesSet'),
      price: '₺899',
      image: 'https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/feb3b70d09f52b5c6a667791b7d77d81.png',
      features: [
        t('pricing.setFeature1'),
        t('pricing.setFeature2'),
        t('pricing.setFeature3'),
        t('pricing.setFeature4'),
        t('pricing.setFeature5')
      ],
      popular: true,
      trendyolLink: 'https://ty.gl/azytgipruspvv'
    },
    {
      name: t('pricing.wellnessSet'),
      price: '₺1,499',
      image: 'https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/dfd57959fbbeb3ed911f0129d055e576.jpeg',
      features: [
        t('pricing.wellnessFeature1'),
        t('pricing.wellnessFeature2'),
        t('pricing.wellnessFeature3'),
        t('pricing.wellnessFeature4'),
        t('pricing.wellnessFeature5')
      ],
      popular: false,
      trendyolLink: 'https://ty.gl/1xexcnsg70rfw'
    }
  ];

  return (
    <section id="pricing" className="py-24 bg-gradient-to-b from-white to-amber-50/30">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-12">
          <span className="text-sm font-medium text-amber-700 tracking-widest uppercase mb-3 block">{t('pricing.label')}</span>
          <h2 className="text-3xl md:text-4xl font-light text-gray-800 mb-3">
            {t('pricing.title')}
          </h2>
          <p className="text-base text-gray-500 max-w-xl mx-auto font-light">
            {t('pricing.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden border ${
                plan.popular
                  ? 'border-amber-400 shadow-lg'
                  : 'border-amber-100 shadow-sm hover:shadow-md hover:border-amber-200'
              } transition-all duration-300`}
            >
              <div className="p-6">
                {plan.popular && (
                  <div className="absolute top-12 left-1/2 transform -translate-x-1/2">
                    <span className="bg-amber-600 text-white px-4 py-1 rounded-full text-xs font-medium whitespace-nowrap">
                      {t('pricing.favorite')}
                    </span>
                  </div>
                )}
                
                {plan.image && (
                  <div className={`flex justify-center ${plan.popular ? 'mt-4' : ''} mb-4`}>
                    <div className="w-20 h-20 rounded-full overflow-hidden border border-amber-100">
                      <img
                        src={plan.image}
                        alt={plan.name}
                        className="w-full h-full object-cover object-bottom scale-110"
                      />
                    </div>
                  </div>
                )}

                <div className="text-center mb-5">
                  <h3 className="text-lg font-medium text-gray-800 mb-1">
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline justify-center gap-1 mt-3">
                    <span className="text-3xl font-light text-gray-800">{plan.price}</span>
                  </div>
                </div>

                <ul className="space-y-2 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-leaf-line text-sm text-amber-600"></i>
                      </div>
                      <span className="text-xs text-gray-600 font-light">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={scrollToOrder}
                  className={`w-full py-3 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    plan.popular
                      ? 'bg-amber-600 text-white hover:bg-amber-700'
                      : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
                  }`}
                >
                  {t('pricing.select')}
                </button>

                <a
                  href={plan.trendyolLink}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  onClick={trackTrendyolConversion}
                  className="flex items-center justify-center gap-1.5 mt-3 text-xs text-orange-600 hover:text-orange-700 transition-colors cursor-pointer font-medium"
                >
                  <i className="ri-shopping-bag-line text-xs"></i>
                  <span className="whitespace-nowrap">{t('pricing.buyOnTrendyol')}</span>
                  <i className="ri-external-link-line text-[10px]"></i>
                </a>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white/60 backdrop-blur-sm rounded-xl p-6 border border-amber-100">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-truck-line text-lg text-amber-600"></i>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-800">{t('pricing.freeShipping')}</h4>
                <p className="text-xs text-gray-500 font-light">{t('pricing.freeShippingDesc')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-leaf-line text-lg text-amber-600"></i>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-800">{t('pricing.ecoFriendly')}</h4>
                <p className="text-xs text-gray-500 font-light">{t('pricing.ecoFriendlyDesc')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-heart-line text-lg text-amber-600"></i>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-800">{t('pricing.madeWithCare')}</h4>
                <p className="text-xs text-gray-500 font-light">{t('pricing.madeWithCareDesc')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
